/* Exec rollup (designs/intake-ui-parity.md UP8) — the PBR picture: RAG,
   committed, demo-critical, and average delivery by market, over the same
   rows the dashboard shows. Pure presentation over rollupByMarket. */
import type { JSX } from "react";
import {
  DELIVERY_TOTAL,
  rollupByMarket,
  summarize,
  type BoardView,
  type RollupRow,
} from "./board-view";

function RagBar({ r }: { r: RollupRow }): JSX.Element {
  return (
    <div className="rag">
      <span className="rag-seg red" title={`${r.red} red`}>
        {r.red}
      </span>
      <span className="rag-seg amber" title={`${r.amber} amber`}>
        {r.amber}
      </span>
      <span className="rag-seg green" title={`${r.green} green`}>
        {r.green}
      </span>
      {r.unset > 0 && (
        <span className="rag-seg unset" title={`${r.unset} unrated`}>
          {r.unset}
        </span>
      )}
    </div>
  );
}

function Cells({ r }: { r: RollupRow }): JSX.Element {
  return (
    <>
      <td>
        <RagBar r={r} />
      </td>
      <td>{r.committed}</td>
      <td>{r.demoCritical}</td>
      <td>
        {/* null only when the group is empty — every domain delivers, so
            there is no longer a "not a delivery question" case (OM10) */}
        {r.avgDelivery === null ? (
          <span className="cell-dash" title="No capabilities in this group.">
            —
          </span>
        ) : (
          `${Math.round(r.avgDelivery * 100)}%`
        )}
      </td>
    </>
  );
}

/* one table, one lens: by market (UF9 — the by-domain lens went with the
   domain registry; a by-owning-team table is the named follow-up) */
function RollupTable({
  title,
  groupLabel,
  groupHelp,
  countLabel,
  rows,
  totals,
  totalLabel,
}: {
  title: string;
  groupLabel: string;
  groupHelp: string;
  countLabel: string;
  rows: RollupRow[];
  totals: RollupRow;
  totalLabel: string;
}): JSX.Element {
  return (
    <div className="card">
      <div className="card-head">
        <h2>{title}</h2>
        <span className="count">{countLabel}</span>
      </div>
      <div className="card-body" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th title={groupHelp}>{groupLabel}</th>
                <th title="Number of capabilities submitted for this group.">Total</th>
                <th title="RAG health counts — Red (off track) / Amber (at risk) / Green (on track). Unrated shown last.">
                  Health (R / A / G)
                </th>
                <th title="Capabilities a platform team has committed to this cycle.">Committed</th>
                <th title="Capabilities targeting the nearest phase-exit demo (phase target p3).">
                  Demo-critical
                </th>
                <th title={`Average delivery-chain completion (of ${DELIVERY_TOTAL}) across this group's asks.`}>
                  Avg delivery
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.market}>
                  <td>{r.market}</td>
                  <td>{r.total}</td>
                  <Cells r={r} />
                </tr>
              ))}
              <tr className="rollup-total">
                <td>
                  <b>{totalLabel}</b>
                </td>
                <td>
                  <b>{totals.total}</b>
                </td>
                <Cells r={totals} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function Rollup({ rows }: { rows: BoardView[] }): JSX.Element {
  const live = rows.filter((r) => !r.archived);
  const markets = rollupByMarket(live);
  const totals = summarize("All markets", live);
  return (
    <div data-intake-rollup>
      <div className="page-head">
        <h1>Exec rollup</h1>
        <p>
          Ask health across the whole queue — the picture for a Profitable Business Review, by{" "}
          <b>who is asking</b> (market).
        </p>
      </div>
      <RollupTable
        title="By market"
        groupLabel="Market / BU"
        groupHelp="Business unit / market the requesting teams roll up to; platform teams carry no market and group under 'Platform'."
        countLabel={`${markets.length} markets · ${totals.total} capabilities`}
        rows={markets}
        totals={totals}
        totalLabel="All markets"
      />
    </div>
  );
}
